import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  analysisApi,
  DocumentAnalysisSnapshot,
  DurableProjectFile,
  getErrorMessage,
  PersistedTaskSnapshot,
  projectFileApi,
  taskApi,
} from '../services/api';
import {
  ACTIVE_TASK_STATUSES,
  useTaskSubscription,
} from '../hooks/useTaskSubscription';

interface DurableDocumentAnalysisProps {
  projectId: number | null;
  fileContent?: string;
  projectFiles?: unknown[];
  projectOverview?: string;
  techRequirements?: string;
  onFileUpload?: (content: string) => void | Promise<void>;
  onFileDeleted?: (fileId: number) => void;
  onAnalysisComplete: (overview: string, requirements: string) => void;
  onConfirmed: (version: number) => void;
}

const FILE_PARSE_SUCCEEDED = 30;

const makeIdempotencyKey = (prefix: string, projectId: number) => {
  const suffix = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${projectId}-${suffix}`;
};

// NOTE: requirementsAsText 函数已弃用，requirements 改为字符串类型
// const requirementsAsText = (requirements: AnalysisRequirement[]) =>
//   requirements
//     .map((item, index) => {
//       const score = item.score_text ? `（${item.score_text}）` : '';
//       return `${index + 1}. ${item.title}${score}\n${item.description}`.trim();
//     })
//     .join('\n\n');

const parseStatusLabel = (file: DurableProjectFile) => {
  if (file.parse_status === 30) return '解析完成';
  if (file.parse_status === 40) return '部分失败';
  if (file.parse_status === 50) return '解析失败';
  if (file.parse_status === 20) return `解析中 ${file.parse_progress}%`;
  if (file.parse_status === 10) return '等待解析';
  return '待解析';
};

const formatFileSize = (bytes: number | null | undefined): string => {
  if (!bytes || bytes <= 0) return '未知大小';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const DurableDocumentAnalysis: React.FC<DurableDocumentAnalysisProps> = ({
  projectId,
  onAnalysisComplete,
  onConfirmed,
}) => {
  const [files, setFiles] = useState<DurableProjectFile[]>([]);
  const [projectVersion, setProjectVersion] = useState(0);
  const [analysis, setAnalysis] = useState<DocumentAnalysisSnapshot | null>(null);
  const [mode, setMode] = useState<'file' | 'paste'>('file');
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [pastedText, setPastedText] = useState('');
  const [overview, setOverview] = useState('');
  const [requirementsText, setRequirementsText] = useState('');
  const [activeTask, setActiveTask] = useState<PersistedTaskSnapshot | null>(null);
  const [taskPurpose, setTaskPurpose] = useState<'file' | 'analysis' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [message, setMessage] = useState('');
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const autoAnalyzedFileIdsRef = useRef<Set<number>>(new Set());
  const autoAnalyzingRef = useRef<boolean>(false);

  // NOTE: parseRequirementsText 函数已弃用，requirements 改为字符串类型
  // const parseRequirementsText = (text: string): AnalysisRequirement[] => {
  //   const lines = text.trim().split('\n').filter(line => line.trim());
  //   const requirements: AnalysisRequirement[] = [];
  //   let currentItem: AnalysisRequirement | null = null;
  //   let descriptionLines: string[] = [];
  //
  //   const finishItem = () => {
  //     if (currentItem) {
  //       currentItem.description = descriptionLines.join('\n').trim();
  //       requirements.push(currentItem);
  //     }
  //   };
  //
  //   for (let i = 0; i < lines.length; i++) {
  //     const line = lines[i].trim();
  //     const match = line.match(/^(\d+)\.\s*(.+?)(?:[（(](.+?)[)）])?\s*$/);
  //
  //     if (match || (!line.includes('：') && !line.includes(':') && line.length < 100)) {
  //       if (match) {
  //         finishItem();
  //         currentItem = {
  //           requirement_code: `REQ-${i + 1}`,
  //           title: match[2] || line,
  //           description: '',
  //           score_text: match[3] || '',
  //           source_location: '',
  //           source_type: 'manual',
  //           sort_order: requirements.length,
  //         };
  //         descriptionLines = [];
  //       } else if (currentItem) {
  //         descriptionLines.push(line);
  //       }
  //     } else if (!currentItem) {
  //       currentItem = {
  //         requirement_code: `REQ-1`,
  //         title: '',
  //         description: line,
  //         score_text: '',
  //         source_location: '',
  //         source_type: 'manual',
  //         sort_order: 0,
  //       };
  //       descriptionLines = [line];
  //     } else {
  //       descriptionLines.push(line);
  //     }
  //   }
  //   finishItem();
  //   return requirements;
  // };

  const applyAnalysis = useCallback((
    value: DocumentAnalysisSnapshot | null
  ) => {
    setAnalysis(value);
    setOverview(value?.overview || '');
    setRequirementsText(value?.requirements || '');
    if (value) {
      setMode(value.analysis_mode);
      setSelectedFileIds(value.source_file_ids);
      onAnalysisComplete(
        value.overview,
        value.requirements || ''
      );
      if (value.confirmed) {
        onConfirmed(value.version_no);
      }
    }
    setIsDirty(false);
  }, [onAnalysisComplete, onConfirmed]);

  const load = useCallback(async (silent = false) => {
    if (projectId === null || projectId === undefined) {
      if (!silent) setIsLoading(false);
      return;
    }
    if (!silent) setIsLoading(true);
    try {
      const [filesResponse, analysisResponse] = await Promise.all([
        projectFileApi.list(projectId),
        analysisApi.get(projectId),
      ]);
      const nextFiles = filesResponse.data.data;
      const nextState = analysisResponse.data.data;
      setFiles(nextFiles);
      setProjectVersion(nextState.project_version);
      applyAnalysis(nextState.analysis);
      if (
        nextState.task
        && ACTIVE_TASK_STATUSES.has(nextState.task.status)
      ) {
        setActiveTask(nextState.task);
        setTaskPurpose('analysis');
      } else {
        setActiveTask(null);
        setTaskPurpose(null);
      }
      if (!nextState.analysis) {
        setSelectedFileIds(
          nextFiles
            .filter((file) => file.parse_status === FILE_PARSE_SUCCEEDED)
            .map((file) => file.id)
        );
      }
      setMessage('');
    } catch (error) {
      setMessage(getErrorMessage(error, '资料解析状态加载失败'));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [applyAnalysis, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  useTaskSubscription(
    activeTask,
    setActiveTask,
    () => {
      void load(true);
    }
  );

  const taskIsActive = Boolean(
    activeTask && ACTIVE_TASK_STATUSES.has(activeTask.status)
  );

  // 自动触发解析：当有新解析完成的文件且没有进行中的任务时
  useEffect(() => {
    // 等待文件列表加载完成
    if (isLoading || !projectId) return;
    
    // 如果有进行中的任务，不自动触发
    if (taskIsActive || autoAnalyzingRef.current) return;
    
    // 如果已经有分析结果且已确认，不自动触发
    if (analysis?.confirmed) return;
    
    // 获取已解析完成的文件
    const parsedFileIds = files
      .filter((file) => file.parse_status === FILE_PARSE_SUCCEEDED)
      .map((file) => file.id);
    
    if (parsedFileIds.length === 0) return;
    
    // 检查是否有新解析完成的文件（之前未处理过的）
    const newFileIds = parsedFileIds.filter(
      (id) => !autoAnalyzedFileIdsRef.current.has(id)
    );
    
    if (newFileIds.length === 0) return;
    
    // 只有当没有已存在的分析结果时才自动触发
    if (analysis && !analysis.confirmed) return;
    
    // 自动触发分析
    autoAnalyzingRef.current = true;
    const newIdsSet = new Set(parsedFileIds);
    
    setSelectedFileIds(parsedFileIds);
    setMessage(`检测到 ${newFileIds.length} 个新解析完成的文件，正在自动分析...`);
    
    const idempotencyKey = `auto-analysis-${projectId}-${Date.now()}`;
    void analysisApi.createFromFiles(projectId, {
      source_file_ids: parsedFileIds,
      base_project_version: projectVersion,
      idempotency_key: idempotencyKey,
    }).then((response) => {
      setActiveTask(response.data.data);
      setTaskPurpose('analysis');
      // 标记这些文件为已处理
      newIdsSet.forEach((id) => autoAnalyzedFileIdsRef.current.add(id));
      setMessage(response.data.msg || '自动分析任务已启动');
    }).catch((error) => {
      setMessage(getErrorMessage(error, '自动分析失败'));
    }).finally(() => {
      autoAnalyzingRef.current = false;
    });
  }, [files, isLoading, projectId, taskIsActive, analysis, projectVersion]);

  const parsedFiles = useMemo(
    () => files.filter(
      (file) => file.parse_status === FILE_PARSE_SUCCEEDED
    ),
    [files]
  );

  const deleteFile = async (file: DurableProjectFile) => {
    if (projectId === null || projectId === undefined) {
      setMessage('项目信息加载中，请稍候再试');
      return;
    }
    if (!window.confirm(`确定删除“${file.file_name}”吗？`)) return;
    setIsSubmitting(true);
    try {
      await projectFileApi.delete(projectId, file.id);
      setFiles((current) => current.filter((item) => item.id !== file.id));
      setSelectedFileIds((current) => current.filter((id) => id !== file.id));
      setMessage(`已删除：${file.file_name}`);
    } catch (error) {
      setMessage(getErrorMessage(error, '资料删除失败'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitAnalysis = async (confirmSwitch: boolean) => {
    if (projectId === null || projectId === undefined) {
      setMessage('项目信息加载中，请稍候再试');
      return;
    }
    setIsSubmitting(true);
    setShowSwitchConfirm(false);
    setMessage('');
    try {
      const idempotencyKey = makeIdempotencyKey('analysis', projectId);
      let response;
      if (analysis && analysis.analysis_mode !== mode) {
        response = await analysisApi.switchMode(projectId, {
          target_mode: mode,
          confirm_switch: confirmSwitch,
          source_file_ids: mode === 'file' ? selectedFileIds : [],
          pasted_text: mode === 'paste' ? pastedText.trim() : '',
          base_project_version: projectVersion,
          idempotency_key: idempotencyKey,
        });
      } else if (mode === 'file') {
        response = await analysisApi.createFromFiles(projectId, {
          source_file_ids: selectedFileIds,
          base_project_version: projectVersion,
          idempotency_key: idempotencyKey,
        });
      } else {
        response = await analysisApi.createFromPaste(projectId, {
          pasted_text: pastedText.trim(),
          base_project_version: projectVersion,
          idempotency_key: idempotencyKey,
        });
      }
      setActiveTask(response.data.data);
      setTaskPurpose('analysis');
      setMessage(response.data.msg);
    } catch (error) {
      setMessage(getErrorMessage(error, '资料分析任务创建失败'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const startAnalysis = () => {
    if (mode === 'file') {
      if (selectedFileIds.length === 0) {
        const availableFileIds = files
          .filter((f) => f.parse_status === FILE_PARSE_SUCCEEDED)
          .map((f) => f.id);
        if (availableFileIds.length > 0) {
          setSelectedFileIds(availableFileIds);
          setMessage(`已自动选中 ${availableFileIds.length} 个解析完成的文件，请确认后点击开始分析`);
          return;
        }
        const pendingCount = files.filter(
          (f) => f.parse_status !== FILE_PARSE_SUCCEEDED
        ).length;
        if (pendingCount > 0) {
          setMessage(`文件尚未解析完成（${pendingCount} 个），请等待解析完成后再试`);
        } else {
          setMessage('请至少上传一个文件');
        }
        return;
      }
    }
    if (mode === 'paste' && !pastedText.trim()) {
      setMessage('请粘贴待解析的招标资料');
      return;
    }
    if (analysis && analysis.analysis_mode !== mode) {
      setShowSwitchConfirm(true);
      return;
    }
    void submitAnalysis(false);
  };

  const saveEdits = async (): Promise<DocumentAnalysisSnapshot | null> => {
    if (projectId === null || projectId === undefined) {
      setMessage('项目信息加载中，请稍候再试');
      return null;
    }
    if (!analysis) return null;
    setIsSubmitting(true);
    setMessage('');
    try {
      const response = await analysisApi.update(projectId, {
        base_analysis_version: analysis.version_no,
        overview: overview.trim(),
        requirements: requirementsText,
      });
      applyAnalysis(response.data.data);
      setMessage(response.data.msg);
      return response.data.data;
    } catch (error) {
      setMessage(getErrorMessage(error, '人工修订保存失败'));
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmAnalysis = async () => {
    if (projectId === null || projectId === undefined) {
      setMessage('项目信息加载中，请稍候再试');
      return;
    }
    if (!analysis) return;
    setIsSubmitting(true);
    setMessage('');
    try {
      let current = analysis;
      if (isDirty) {
        const saved = await saveEdits();
        if (!saved) return;
        current = saved;
      }
      const response = await analysisApi.confirm(
        projectId,
        current.version_no
      );
      applyAnalysis(response.data.data);
      onAnalysisComplete(
        response.data.data.overview,
        response.data.data.requirements || ''
      );
      onConfirmed(response.data.data.version_no);
      setMessage(response.data.msg);
    } catch (error) {
      setMessage(getErrorMessage(error, '解析结果确认失败'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="h-44 animate-pulse rounded-2xl bg-white" />
        <div className="h-[420px] animate-pulse rounded-2xl bg-white" />
      </div>
    );
  }

  if (projectId === null || projectId === undefined) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-700">
        项目信息加载中，请稍候...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <section className="bid-surface p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900">项目资料</h3>
            <p className="mt-1 text-sm text-slate-500">
              此处展示在「标书设置」中上传的招标文件，等待解析完成后即可创建资料分析任务。
            </p>
          </div>
        </div>

        {files.length === 0 ? (
          <div className="mt-5 grid min-h-32 place-items-center rounded-xl border border-dashed border-slate-200 text-sm text-slate-400">
            暂无项目资料
          </div>
        ) : (
          <div className="mt-5">
            <div className="mb-2 flex items-center gap-3 text-xs text-slate-500">
              <span>共 {files.length} 个文件</span>
              {parsedFiles.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedFileIds(parsedFiles.map((f) => f.id))}
                  className="rounded-md bg-slate-100 px-2 py-1 hover:bg-slate-200"
                >
                  全选解析完成（{parsedFiles.length}）
                </button>
              )}
              {selectedFileIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedFileIds([])}
                  className="rounded-md bg-slate-100 px-2 py-1 hover:bg-slate-200"
                >
                  清空选择
                </button>
              )}
            </div>
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
            {files.map((file) => (
              <div key={file.id} className="flex items-center gap-4 px-4 py-3">
                <input
                  type="checkbox"
                  aria-label={`选择 ${file.file_name}`}
                  disabled={file.parse_status !== FILE_PARSE_SUCCEEDED}
                  checked={selectedFileIds.includes(file.id)}
                  onChange={(event) => {
                    setSelectedFileIds((current) => event.target.checked
                      ? [...current, file.id]
                      : current.filter((id) => id !== file.id));
                  }}
                />
                <DocumentTextIcon className="h-8 w-8 text-slate-300" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-700">
                    {file.file_name}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    {parseStatusLabel(file)} · {formatFileSize(file.file_size)}
                  </div>
                  {file.parse_error_message && (
                    <div className="mt-1 text-xs text-red-500">
                      {file.parse_error_message}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => void deleteFile(file)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-500"
                  aria-label={`删除 ${file.file_name}`}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
            </div>
          </div>
        )}
      </section>

      <section className="bid-surface p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex rounded-xl bg-slate-100 p-1">
            {(['file', 'paste'] as const).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setMode(item)}
                className={`rounded-lg px-4 py-2 text-sm ${
                  mode === item
                    ? 'bg-white font-medium text-slate-800 shadow-sm'
                    : 'text-slate-500'
                }`}
              >
                {item === 'file' ? '文件解析' : '粘贴解析'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={startAnalysis}
            disabled={isSubmitting || taskIsActive}
            className="bid-primary-button inline-flex items-center gap-2"
          >
            <ArrowPathIcon className="h-4 w-4" />
            {analysis ? '重新分析' : '开始分析'}
          </button>
        </div>

        {mode === 'paste' && (
          <textarea
            value={pastedText}
            onChange={(event) => setPastedText(event.target.value)}
            placeholder="粘贴招标文件正文，最多 200 万字符"
            className="mt-5 min-h-48 w-full rounded-xl border border-slate-200 p-4 text-sm outline-none focus:border-[var(--bid-color-primary)]"
          />
        )}
        {mode === 'file' && parsedFiles.length === 0 && (
          <div className="mt-5 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
            暂无解析完成的文件，请先上传并等待文件解析任务完成。
          </div>
        )}

        {activeTask && (
          <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-700">
                {taskPurpose === 'file' ? '文件解析' : '资料分析'} · {activeTask.status_name}
              </span>
              <span className="text-slate-500">{activeTask.progress_percent}%</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-[var(--bid-color-primary)] transition-all"
                style={{ width: `${activeTask.progress_percent}%` }}
              />
            </div>
            <div className="mt-2 text-xs text-slate-400">
              已完成 {activeTask.completed_units}/{activeTask.total_units}
              {activeTask.error_message ? ` · ${activeTask.error_message}` : ''}
            </div>
            {[0, 10, 20].includes(activeTask.status) && (
              <button
                type="button"
                onClick={async () => {
                  const response = await taskApi.cancel(activeTask.id);
                  setActiveTask(response.data.data);
                  setMessage(response.data.msg);
                }}
                className="mt-3 text-xs font-medium text-red-500"
              >
                取消任务
              </button>
            )}
          </div>
        )}
      </section>

      {analysis ? (
        <section className="bid-surface p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-slate-900">
                解析结果 v{analysis.version_no}
              </h3>
              <p className="mt-1 text-xs text-slate-400">
                {analysis.confirmed ? '已确认' : '未确认'} · {analysis.analysis_mode === 'file' ? '文件解析' : '粘贴解析'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                disabled={isSubmitting || !isDirty}
                onClick={() => void saveEdits()}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600 disabled:opacity-40"
              >
                保存修订
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => void confirmAnalysis()}
                className="bid-primary-button inline-flex items-center gap-2"
              >
                <CheckCircleIcon className="h-4 w-4" />
                {analysis.confirmed && !isDirty ? '已确认' : '确认解析结果'}
              </button>
            </div>
          </div>

          <label className="mt-6 block text-sm font-medium text-slate-700">
            项目概述
          </label>
          <textarea
            value={overview}
            onChange={(event) => {
              setOverview(event.target.value);
              setIsDirty(true);
            }}
            className="mt-2 min-h-40 w-full rounded-xl border border-slate-200 p-4 text-sm leading-6 outline-none focus:border-[var(--bid-color-primary)]"
          />

          <label className="mt-6 block text-sm font-medium text-slate-700">
            技术评分要求
          </label>
          <p className="mt-1 text-xs text-slate-400">
            系统已自动提取技术评分要求，您可以进行编辑修改。格式支持编号列表（如：1. 标题（分值））
          </p>
          <textarea
            value={requirementsText}
            onChange={(event) => {
              setRequirementsText(event.target.value);
              setIsDirty(true);
            }}
            className="mt-2 min-h-64 w-full rounded-xl border border-slate-200 p-4 text-sm leading-6 outline-none focus:border-[var(--bid-color-primary)]"
          />
        </section>
      ) : (
        <section className="grid min-h-52 place-items-center rounded-2xl border border-dashed border-slate-200 bg-white text-center">
          <div>
            <DocumentTextIcon className="mx-auto h-12 w-12 text-slate-300" />
            <div className="mt-3 text-sm font-medium text-slate-600">尚无解析结果</div>
            <div className="mt-1 text-xs text-slate-400">选择资料或粘贴正文后开始分析</div>
          </div>
        </section>
      )}

      {message && (
        <div className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${
          /失败|错误|不能为空|请选择|暂无/.test(message)
            ? 'bg-red-50 text-red-600'
            : 'bg-emerald-50 text-emerald-700'
        }`}>
          {/失败|错误|不能为空|请选择|暂无/.test(message)
            ? <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            : <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />}
          {message}
        </div>
      )}

      {showSwitchConfirm && (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-slate-950/40 p-5">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="font-semibold text-slate-900">确认切换解析方式</h3>
            <p className="mt-3 text-sm leading-6 text-slate-500">
              切换后会创建新的分析版本，当前已确认结果仍保留在历史版本中。
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowSwitchConfirm(false)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => void submitAnalysis(true)}
                className="bid-primary-button"
              >
                确认切换
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DurableDocumentAnalysis;
